import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { IDatasource } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
// import DottedMap from 'dotted-map/without-countries';
// import worldMapData from '../acquirers/worldMapData.json';
// import { REGION_FLAGS, resolveCountryGeo } from '../acquirers/constants/constants';
import './Merchants.css';
import '../transaction-monitoring/TransactionMonitoring.css';
import StatusBadge from '../../components/shared/StatusBadge';
import DeleteConfirmDialog, { type ConfirmDetailRow } from '../../components/shared/DeleteConfirmDialog';
import StatusUpdateDialog from '../../components/shared/StatusUpdateDialog';
import { DataGrid, type ColDef, type GridApi } from '../../components/ui/table';
import { ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import { GridColumnFilterPanel, formatDateRangeFilterLabel } from '../../components/shared/GridColumnFilterPanel';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { commonTextFilterParams } from '../../lib/agGridOptions';
import {
  formatEntityStatusLabel,
  // matchesEntityStatus,
  toApiEntityStatus,
} from '../../utils/status';
import { showToast } from '../../store/toastStore';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import {
  // BsShop,
  // BsCheckCircle,
  BsPlusCircle,
} from 'react-icons/bs';
import {
  MERCHANT_COLUMN_LABELS,
  buildMerchantColumnFilterFields,
  createMerchantColumnDefs,
} from './table/columnDefs';
import { merchantGridOptions, GRID_PAGE_SIZE, GRID_PAGE_SIZE_SELECTOR } from './table/gridOptions';
import MerchantFilters from './components/MerchantFilters';
import MerchantToolbar from './components/MerchantToolbar';
import {
  deleteMerchant,
  fetchMerchantsGrid,
  fetchMerchantsList,
  updateMerchantStatus,
} from './api/api';
import { buildMerchantGridRows, buildMerchantsRequest, mapAgGridFilterModelToMerchantFilters } from './utils/utils';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import { ACCENT_COLORS, SEARCH_DEBOUNCE_MS } from './constants/constants';
import { type MerchantColumnFilterKey, type MerchantColumnFilters, type MerchantGridRow, type MerchantRow } from './models/types';
import { EMPTY_MERCHANT_COLUMN_FILTERS } from './constants/constants';

function buildMerchantDeleteDetails(merchant: MerchantRow): ConfirmDetailRow[] {
  return [
    { label: 'Merchant', value: merchant.name },
    { label: 'Email', value: merchant.email },
    { label: 'Country', value: merchant.country },
    { label: 'Category', value: merchant.category },
    { label: 'Status', value: <StatusBadge status={merchant.status} /> },
    { label: 'KYC', value: merchant.kyc },
    { label: 'Total MIDs', value: merchant.mids > 0 ? merchant.mids.toLocaleString() : '—' },
    {
      label: 'Volume',
      value: merchant.volume > 0 ? `€${merchant.volume.toLocaleString()}` : '—',
    },
    { label: 'Joined', value: merchant.joined },
  ];
}

function buildMerchantStatusDetails(merchant: MerchantRow): ConfirmDetailRow[] {
  return [
    { label: 'Merchant', value: merchant.name },
    { label: 'Email', value: merchant.email },
    { label: 'Country', value: merchant.country },
  ];
}

const MERCHANT_GRID_DEFAULT_COL_DEF = {
  suppressAutoSize: true,
  filter: {
    component: 'agTextColumnFilter',
    doesFilterPass: () => true,
  },
  filterParams: {
    ...commonTextFilterParams,
    filterOptions: ['contains'],
  },
};

const Merchants: React.FC = () => {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<MerchantRow[]>([]);
  const [statsMerchants, setStatsMerchants] = useState<MerchantRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [columnFilters, setColumnFilters] = useState<MerchantColumnFilters>(
    EMPTY_MERCHANT_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // Bumped when AG Grid's header filters change so ActiveGridFiltersBar can re-read
  // getFilterModel() without putting those values into React state.
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  // When true, the next columnFilters-driven datasource reset is skipped because the
  // change originated from clearing/syncing the AG Grid filter model itself.
  const skipDatasourceResetRef = useRef(false);
  const gridApiRef = useRef<GridApi<MerchantGridRow> | null>(null);
  const [exporting, setExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MerchantRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState<MerchantRow | null>(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const tableFiltersRef = useRef({
    filterStatus,
    search: debouncedSearch,
    columnFilters,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleRemoveColumnFilter = useCallback((key: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      skipDatasourceResetRef.current = true;
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_MERCHANT_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_MERCHANT_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      skipDatasourceResetRef.current = true;
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback(
    (values: Record<string, string[] | null>) => {
      const nextColumnFilters = {
        ...EMPTY_MERCHANT_COLUMN_FILTERS,
        ...(Object.fromEntries(
          (Object.keys(EMPTY_MERCHANT_COLUMN_FILTERS) as MerchantColumnFilterKey[]).map(
            (key) => [key, values[key]?.length ? values[key] : null],
          ),
        ) as MerchantColumnFilters),
      };
      tableFiltersRef.current = {
        ...tableFiltersRef.current,
        columnFilters: nextColumnFilters,
      };
      gridApiRef.current?.setFilterModel(null);
      setColumnFilters(nextColumnFilters);
    },
    [],
  );

  useEffect(() => {
    tableFiltersRef.current = {
      filterStatus,
      search: debouncedSearch,
      columnFilters,
    };
  }, [filterStatus, debouncedSearch, columnFilters]);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = pageSize > 0 ? Math.floor(params.startRow / pageSize) : 0;
        setLoading(true);
        setError(null);
        try {
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const nativeColumnFilters = mapAgGridFilterModelToMerchantFilters(rawFilterModel);
          const mergedColumnFilters: MerchantColumnFilters = {
            ...tableFiltersRef.current.columnFilters,
            ...nativeColumnFilters,
          };
          const { rows, totalElements } = await fetchMerchantsList(
            buildMerchantsRequest({
              filterStatus: tableFiltersRef.current.filterStatus,
              search: tableFiltersRef.current.search,
              columnFilters: mergedColumnFilters,
              page,
              size: pageSize,
            }),
          );
          setMerchants(rows);
          params.successCallback(buildMerchantGridRows(rows, ACCENT_COLORS), totalElements);
        } catch (err: any) {
          console.error('Failed to fetch merchants:', err);
          setError(err.response?.data?.message || err.message || 'Failed to load merchants');
          setMerchants([]);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption('datasource', tableDatasource);
  }, [filterStatus, debouncedSearch, columnFilters, tableDatasource]);

  const fetchStatsMerchants = useCallback(async () => {
    try {
      const { rows } = await fetchMerchantsGrid({
        filterStatus: 'All',
        search: '',
        columnFilters: EMPTY_MERCHANT_COLUMN_FILTERS,
      });
      setStatsMerchants(rows);
    } catch (err) {
      console.error('Failed to fetch merchant stats:', err);
    }
  }, []);

  const refreshMerchants = useCallback(async () => {
    gridApiRef.current?.refreshInfiniteCache();
    await fetchStatsMerchants();
  }, [fetchStatsMerchants]);

  useAsyncEffect(async (isActive) => {
    await fetchStatsMerchants();
    if (!isActive()) return;
  }, [fetchStatsMerchants]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToMerchantFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      await downloadExcelExport({
        type: EXPORT_TYPES.MERCHANTS,
        fileNamePrefix: 'merchants',
        errorMessage: 'Failed to export merchants.',
        payload: buildMerchantsRequest({
          filterStatus,
          search: debouncedSearch,
          columnFilters: {
            ...columnFilters,
            ...nativeColumnFilters,
          },
        }),
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteClick = (merchant: MerchantRow) => {
    setDeleteError(null);
    setDeleteTarget(merchant);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const merchantName = deleteTarget.name;
    setDeleting(true);
    setDeleteError(null);
    setError(null);
    try {
      await deleteMerchant(deleteTarget.id);
      setDeleteTarget(null);
      showToast(`Merchant "${merchantName}" has been deleted.`, 'success');
      await refreshMerchants();
    } catch (err: any) {
      console.error('Failed to delete merchant:', err);
      const message = err.response?.data?.message || 'Failed to delete merchant';
      setDeleteError(message);
      showToast(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusClick = (merchant: MerchantRow) => {
    setStatusError(null);
    setStatusTarget(merchant);
    setSelectedStatus(toApiEntityStatus(merchant.status));
  };

  const closeStatusDialog = () => {
    if (statusUpdating) return;
    setStatusTarget(null);
    setStatusError(null);
  };

  const confirmStatusUpdate = async () => {
    if (!statusTarget) return;

    const merchantName = statusTarget.name;
    const statusLabel = formatEntityStatusLabel(selectedStatus);
    setStatusUpdating(true);
    setStatusError(null);
    setError(null);
    try {
      await updateMerchantStatus(statusTarget.id, selectedStatus);
      setStatusTarget(null);
      showToast(`${merchantName} status updated to ${statusLabel}.`, 'success');
      await refreshMerchants();
    } catch (err: any) {
      console.error('Failed to update merchant status:', err);
      const message = err.response?.data?.message || 'Failed to update merchant status';
      setStatusError(message);
      showToast(message, 'error');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleGridFilterChanged = useCallback(() => {
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi<MerchantGridRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [tablePageSize]);

  const mergedColumnFilters = useMemo<MerchantColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToMerchantFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [MerchantColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: MERCHANT_COLUMN_LABELS[key],
        value:
          key === 'createdAt'
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const fields = buildMerchantColumnFilterFields(
      statsMerchants.length > 0 ? statsMerchants : merchants,
    );
    return fields.map((field) => {
      if (field.type === 'dateRange') return field;
      const applied = mergedColumnFilters[field.key as MerchantColumnFilterKey];
      if (!applied?.length) return field;
      const missing = applied.filter((value) => !field.options.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing],
      };
    });
  }, [merchants, statsMerchants, mergedColumnFilters]);

  // const totalElements = statsMerchants.length;
  // const networkMerchants = statsMerchants;

  // const isCreatedThisMonth = (createdAt?: string) => {
  //   if (!createdAt) return false;
  //   const created = new Date(createdAt);
  //   const now = new Date();
  //   return (
  //     created.getMonth() === now.getMonth() &&
  //     created.getFullYear() === now.getFullYear()
  //   );
  // };

  // const totalCount = totalElements || 1;
  // const activeCount = networkMerchants.filter((m) =>
  //   matchesEntityStatus(m.status, 'Active'),
  // ).length;
  // const joinedThisMonth = networkMerchants.filter((m) =>
  //   isCreatedThisMonth(m.createdAt),
  // ).length;

  // Dotted world map + region breakdown, built live from the countries merchants report
  // const footprint = useMemo(() => {
  //   const mapMeta = worldMapData as unknown as {
  //     width: number;
  //     height: number;
  //   };

  //   const byCountry = new Map<string, number>();
  //   networkMerchants.forEach((m) => {
  //     if (m.country && m.country !== '—') {
  //       byCountry.set(m.country, (byCountry.get(m.country) || 0) + 1);
  //     }
  //   });

  //   const byRegion = new Map<string, Set<string>>();
  //   byCountry.forEach((_count, country) => {
  //     const geo = resolveCountryGeo(country);
  //     if (!geo) return;
  //     if (!byRegion.has(geo.region)) byRegion.set(geo.region, new Set());
  //     byRegion.get(geo.region)!.add(country);
  //   });

  //   const regions = Array.from(byRegion.entries())
  //     .map(([name, countries]) => ({
  //       name,
  //       countries: countries.size,
  //       flag: REGION_FLAGS[name] || '🌍',
  //     }))
  //     .sort((a, b) => b.countries - a.countries);

  //   const map = new DottedMap({ map: worldMapData as any });
  //   const pins: Array<{
  //     x: number;
  //     y: number;
  //     country: string;
  //     region: string;
  //     weight: number;
  //   }> = [];

  //   byCountry.forEach((weight, country) => {
  //     const geo = resolveCountryGeo(country);
  //     if (!geo) return;
  //     const point = map.addPin({
  //       lat: geo.lat,
  //       lng: geo.lng,
  //       svgOptions: { color: '#29D4FF', radius: 0.7 },
  //     });
  //     pins.push({ x: point.x, y: point.y, country, region: geo.region, weight });
  //   });

  //   const svg = map.getSVG({
  //     radius: 0.24,
  //     color: 'rgba(104,115,128,0.35)',
  //     shape: 'circle',
  //     backgroundColor: 'transparent',
  //   });

  //   // Hub-and-spoke routes: connect the busiest country to the top country of every other region
  //   const hub = pins.slice().sort((a, b) => b.weight - a.weight)[0];
  //   const topPerRegion = new Map<string, (typeof pins)[number]>();
  //   pins.forEach((p) => {
  //     const current = topPerRegion.get(p.region);
  //     if (!current || p.weight > current.weight) topPerRegion.set(p.region, p);
  //   });

  //   const routes: Array<[(typeof pins)[number], (typeof pins)[number]]> = [];
  //   if (hub) {
  //     topPerRegion.forEach((p) => {
  //       if (p.country !== hub.country) routes.push([hub, p]);
  //     });
  //   }

  //   return {
  //     svg,
  //     width: mapMeta.width,
  //     height: mapMeta.height,
  //     pins,
  //     routes,
  //     regions,
  //   };
  // }, [networkMerchants]);

  // const kpis = [
  //   {
  //     label: 'Total Merchants',
  //     value: totalElements.toString(),
  //     sub: joinedThisMonth > 0 ? `This month +${joinedThisMonth}` : 'This month +0',
  //     color: '#5c6bc0',
  //     icon: <BsShop />,
  //     pct: 100,
  //   },
  //   {
  //     label: 'Active',
  //     value: activeCount.toString(),
  //     sub: 'Base',
  //     color: '#2ed573',
  //     icon: <BsCheckCircle />,
  //     pct: Math.round((activeCount / totalCount) * 100),
  //   },
  // ];

  const merchantColumnDefs = useMemo<ColDef<MerchantGridRow>[]>(
    () => createMerchantColumnDefs(),
    [],
  );

  const merchantGridContext = useMemo(
    () => ({
      actions: {
        onStatus: handleStatusClick,
        onEdit: (m: MerchantGridRow) => navigate(`/dashboard/merchants/edit/${m.id}`),
        onDelete: handleDeleteClick,
        statusLabel: 'Change status',
        editLabel: 'Edit merchant',
        deleteLabel: 'Delete merchant',
      },
    }),
    [navigate],
  );

  const merchantGridOptionsMemo = useMemo(
    () => ({
      ...merchantGridOptions,
      getRowId: (params: { data?: MerchantGridRow }) => String(params.data?.id ?? ''),
      onPaginationChanged: handleTablePaginationChanged,
      onFilterChanged: handleGridFilterChanged,
    }),
    [handleTablePaginationChanged, handleGridFilterChanged],
  );

  const gridLoading = loading;

  return (
    <div className="tm-page dh-page-fill">
      <header className="tm-console-header">
        <div className="tm-console-header-row">
          <div className="tm-console-header-copy">
            <h1 className="tm-console-title">Merchants</h1>
          </div>
          <div className="tm-console-header-actions">
            <Button
              type="button"
              size="sm"
              className="tm-console-action-btn gap-1.5"
              onClick={() => navigate('/dashboard/merchants/create')}
            >
              <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
              Create Merchant
            </Button>
          </div>
        </div>
      </header>

      {error ? <div className="tm-error-banner">{error}</div> : null}

      {/* <div className="mm-kpi-row">
        {kpis.map((k) => (
          <div
            className="tm-kpi-card"
            key={k.label}
            style={{ '--kpi-color': k.color } as React.CSSProperties}
          >
            <div className="tm-kpi-top">
              <span className="tm-kpi-label">{k.label}</span>
            </div>
            <div className="tm-kpi-value">{k.value}</div>
            <div className="tm-kpi-hint">{k.sub}</div>
            <div className="tm-kpi-rule" />
          </div>
        ))}
      </div>

      <div className="acq-map-row">
        <div className="dh-chart-card acq-map-card">
          <div className="dh-table-header">
            <div>
              <span className="acq-map-eyebrow">Geographic Coverage</span>
              <div className="dh-chart-title">Global merchant footprint</div>
            </div>
            <span className="dh-period-pill">
              <span className="dh-dot" style={{ background: '#3b82f6' }} />{' '}
              Routes
            </span>
          </div>

          <div className="acq-map-svg-wrap">
            <img
              className="acq-map-bg"
              src={`data:image/svg+xml;utf8,${encodeURIComponent(footprint.svg)}`}
              alt=""
            />
            <svg
              className="acq-map-svg"
              viewBox={`0 0 ${footprint.width} ${footprint.height}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <radialGradient id="mmNodeGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#29D4FF" stopOpacity="0.65" />
                  <stop offset="100%" stopColor="#29D4FF" stopOpacity="0" />
                </radialGradient>
              </defs>

              {footprint.routes.map(([from, to], i) => {
                const mx = (from.x + to.x) / 2;
                const my = Math.min(from.y, to.y) - footprint.height * 0.16;
                return (
                  <path
                    key={`r-${i}`}
                    d={`M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`}
                    className="acq-map-route"
                  />
                );
              })}
              {footprint.pins.map((n) => (
                <g key={n.country}>
                  <circle cx={n.x} cy={n.y} r={1.6} fill="url(#mmNodeGlow)" />
                  <circle cx={n.x} cy={n.y} r={0.55} className="acq-map-node" />
                </g>
              ))}
            </svg>
          </div>

          <div className="acq-region-row">
            {footprint.regions.length === 0 ? (
              <span className="acq-region-empty">
                No country data available yet
              </span>
            ) : (
              footprint.regions.map((r) => (
                <div className="acq-region-chip" key={r.name}>
                  <div>
                    <span className="acq-region-eyebrow">
                      {r.name.toUpperCase()}
                    </span>
                    <span className="acq-region-value">
                      {r.countries}{' '}
                      {r.countries === 1 ? 'country' : 'countries'}
                    </span>
                  </div>
                  <span className="acq-region-badge">{r.flag}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div> */}

      <div className="dh-table-section tm-table-sections">
        <Card className="tm-table-card tm-table-toolbar-card">
          <div className="tm-table-panel">
            <div className="tm-table-toolbar-row">
              <MerchantFilters
                filterStatus={filterStatus}
                onFilterStatusChange={setFilterStatus}
                loading={gridLoading}
                statsMerchants={statsMerchants}
              />
              <div className="tm-table-toolbar-actions">
                <MerchantToolbar
                  search={search}
                  onSearchChange={setSearch}
                  activeColumnFilterCount={activeColumnFilters.length}
                  onOpenFilters={() => setFilterPanelOpen(true)}
                  onExport={handleExport}
                  exporting={exporting}
                  loading={gridLoading}
                  hasRows={merchants.length > 0}
                />
              </div>
            </div>
          </div>
        </Card>
        <Card className="tm-table-card tm-table-grid-card overflow-hidden">
          <ActiveGridFiltersBar
            filters={activeColumnFilters}
            onRemove={handleRemoveColumnFilter}
            onClearAll={handleClearColumnFilters}
          />
          <DataGrid<MerchantGridRow>
            rowModelType="infinite"
            datasource={tableDatasource}
            cacheBlockSize={tablePageSize}
            columnDefs={merchantColumnDefs}
            context={merchantGridContext}
            loading={gridLoading}
            pagination
            paginationPageSize={tablePageSize}
            paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
            height="100%"
            className="tm-ag-grid"
            onGridReady={handleGridReady}
            defaultColDef={MERCHANT_GRID_DEFAULT_COL_DEF}
            gridOptions={merchantGridOptionsMemo}
          />
        </Card>
      </div>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        title="Delete Merchant"
        entityLabel="merchant"
        entityName={deleteTarget?.name || 'Unknown'}
        details={deleteTarget ? buildMerchantDeleteDetails(deleteTarget) : []}
        loading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
      />

      <StatusUpdateDialog
        open={!!statusTarget}
        title="Update Merchant Status"
        entityLabel="merchant"
        entityName={statusTarget?.name || 'Unknown'}
        currentStatus={statusTarget?.status}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        details={statusTarget ? buildMerchantStatusDetails(statusTarget) : []}
        loading={statusUpdating}
        error={statusError}
        onConfirm={confirmStatusUpdate}
        onCancel={closeStatusDialog}
      />

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />
    </div>
  );
};

export default Merchants;
